/**
 * Tutorial and help-support form state, plus the role choices shown in the picker.
 */

import {
  HELP_PHONE_FALLBACK,
  HELP_SUPPORT_FORM_SECTIONS,
  HELP_URL_FALLBACK,
} from '@/components/tutorials/tutorial-configs';
import { SUPPORT_TYPE_LABEL } from '@/constants/shared-catalog';
import type { Role, RoleMode } from '@/types/role.types';
import type {
  HelpSupportContextEntry,
  HelpSupportEntry,
  HelpSupportMode,
  HelpSupportPlatform,
  TutorialVideo,
  TutorialVideoFormState,
  TutorialVideoMode,
} from '@/types/tutorial.types';
import { roleKind, roleMode } from '@/utils/role-label-utils';

/** One multiselect option. One option persists one role id. */
export interface TutorialRoleChoice {
  key: string;
  label: string;
  roleIds: string[];
}

/** Help-support field the superadmin can patch. */
export type HelpSupportEntryField = 'phone' | 'url';

/** Role codes always attached on save and hidden from the picker. */
const HIDDEN_AUDIENCE_ROLE_CODES = new Set(['admin', 'superadmin']);

/** Empty create form. Administration is attached by the API. */
export const emptyTutorialVideoForm: TutorialVideoFormState = {
  name: '',
  mode: 'hybrid',
  roleIds: [],
  file: null,
};

/** True when the string is an http(s) URL. */
export function isTutorialHttpUrl(value: string): boolean {
  return /^https?:\/\/\S+$/i.test(value.trim());
}

/** Map a loaded tutorial onto the edit form. The file is optional on edit. */
export function tutorialVideoToForm(video: TutorialVideo): TutorialVideoFormState {
  return {
    name: video.name,
    mode: video.mode,
    roleIds: video.roleIds,
    file: null,
  };
}

/** Name, mode, and a video file are required to create. */
export function isTutorialVideoCreateFormValid(form: TutorialVideoFormState): boolean {
  return Boolean(form.name.trim() && form.mode && form.file);
}

/** Name and mode are required to edit; the file stays optional. */
export function isTutorialVideoEditFormValid(form: TutorialVideoFormState): boolean {
  return Boolean(form.name.trim() && form.mode);
}

/** True when every help row has a phone and an http(s) form URL. */
export function isHelpSupportCatalogValid(entries: HelpSupportContextEntry[]): boolean {
  return (
    entries.length === 4 &&
    entries.every((entry) => entry.phone.trim().length > 0 && isTutorialHttpUrl(entry.url))
  );
}

/** `platform:mode` key used by the help-support form. */
export function helpSupportEntryKey(
  entry: Pick<HelpSupportContextEntry, 'platform' | 'mode'>,
): string {
  return `${entry.platform}:${entry.mode}`;
}

/** Index the loaded rows by `platform:mode`. */
export function helpSupportEntriesByKey(
  entries: HelpSupportContextEntry[],
): Record<string, HelpSupportContextEntry> {
  return Object.fromEntries(entries.map((entry) => [helpSupportEntryKey(entry), entry]));
}

/** Replace one field of the row that matches `platform:mode`. */
export function updateHelpSupportEntry(
  entries: HelpSupportContextEntry[],
  key: string,
  field: HelpSupportEntryField,
  value: string,
): HelpSupportContextEntry[] {
  return entries.map((entry) =>
    helpSupportEntryKey(entry) === key ? { ...entry, [field]: value } : entry,
  );
}

/** Phone and URL shown before the help-support API responds. */
export function helpSupportFallback(
  platform: HelpSupportPlatform,
  mode: HelpSupportMode,
): HelpSupportEntry {
  return { phone: HELP_PHONE_FALLBACK, url: HELP_URL_FALLBACK[platform][mode] };
}

/** The four fallback rows, used before the admin help API responds. */
export function helpSupportFallbackCatalog(): HelpSupportContextEntry[] {
  return HELP_SUPPORT_FORM_SECTIONS.map((section) => ({
    platform: section.platform,
    mode: section.mode,
    ...helpSupportFallback(section.platform, section.mode),
  }));
}

/** Dropzone preview of the already-saved file. Empty when the user picked a replacement. */
export function tutorialStoredVideoPreview(
  form: TutorialVideoFormState,
  video: TutorialVideo | undefined,
): { name?: string; url?: string } {
  if (form.file || !video) return {};
  return { name: video.name, url: video.videoUrl };
}

/** Patch the selected video file. */
export function applyTutorialVideoFile(
  form: TutorialVideoFormState,
  file: File | null,
): TutorialVideoFormState {
  return { ...form, file };
}

/** Patch the display name. */
export function applyTutorialVideoName(
  form: TutorialVideoFormState,
  name: string,
): TutorialVideoFormState {
  return { ...form, name };
}

/** Switch mode and drop role ids the new mode no longer offers. */
export function applyTutorialVideoMode(
  form: TutorialVideoFormState,
  mode: TutorialVideoMode,
  roles: Role[] | undefined,
): TutorialVideoFormState {
  const roleIds = roles ? roleIdsAllowedForMode(roles, mode, form.roleIds) : form.roleIds;
  return { ...form, mode, roleIds };
}

/** Persist the role ids that match the selected picker keys. */
export function applyTutorialVideoRoleKeys(
  form: TutorialVideoFormState,
  choices: TutorialRoleChoice[],
  keys: string[],
): TutorialVideoFormState {
  return { ...form, roleIds: roleIdsFromChoiceKeys(choices, keys) };
}

/** MultiSelect options from the role choices of the current mode. */
export function tutorialRoleSelectOptions(
  choices: TutorialRoleChoice[],
): Array<{ value: string; label: string }> {
  return choices.map((choice) => ({ value: choice.key, label: choice.label }));
}

/** True when this role must stay out of the picker. */
function isHiddenAudienceRole(role: Role): boolean {
  return HIDDEN_AUDIENCE_ROLE_CODES.has(role.code.trim().toLowerCase());
}

/** True when the role can be offered for the video mode. */
function roleMatchesVideoMode(
  mode: RoleMode | null | undefined,
  videoMode: TutorialVideoMode,
): boolean {
  if (videoMode === 'hybrid') return true;
  if (mode == null || mode === 'hybrid') return true;
  return mode === videoMode;
}

/** Label plus `(Présentiel)` / `(Distanciel)` when the same role exists in both modes. */
function roleChoiceLabel(role: Role, duplicated: boolean): string {
  const base = role.label || role.code;
  if (!duplicated) return base;
  const mode = roleMode(role.code, role.mode ?? null);
  const modeLabel = mode ? SUPPORT_TYPE_LABEL[mode] : undefined;
  if (!modeLabel) return base;
  return `${base} (${modeLabel})`;
}

/**
 * Role options for the video mode.
 * Hybrid keeps présentiel and distanciel as separate choices, with the mode in parentheses
 * when both exist, same as Créer collaborateur.
 */
export function buildTutorialRoleChoices(
  roles: Role[],
  videoMode: TutorialVideoMode,
): TutorialRoleChoice[] {
  const eligible = roles.filter(
    (role) => !isHiddenAudienceRole(role) && roleMatchesVideoMode(role.mode, videoMode),
  );
  const kindCounts = new Map<string, number>();
  if (videoMode === 'hybrid') {
    for (const role of eligible) {
      const kind = roleKind(role.code);
      kindCounts.set(kind, (kindCounts.get(kind) ?? 0) + 1);
    }
  }

  return eligible.map((role) => ({
    key: role.id,
    label: roleChoiceLabel(
      role,
      videoMode === 'hybrid' && (kindCounts.get(roleKind(role.code)) ?? 0) > 1,
    ),
    roleIds: [role.id],
  }));
}

/** Choice keys currently selected from stored role ids. */
export function selectedChoiceKeys(choices: TutorialRoleChoice[], roleIds: string[]): string[] {
  const selected = new Set(roleIds);
  return choices
    .filter((choice) => choice.roleIds.some((id) => selected.has(id)))
    .map((choice) => choice.key);
}

/** Role ids to persist for the selected choice keys. */
export function roleIdsFromChoiceKeys(choices: TutorialRoleChoice[], keys: string[]): string[] {
  const selected = new Set(keys);
  return choices.filter((choice) => selected.has(choice.key)).flatMap((choice) => choice.roleIds);
}

/** Drop role ids that the new mode no longer offers. */
export function roleIdsAllowedForMode(
  roles: Role[],
  videoMode: TutorialVideoMode,
  roleIds: string[],
): string[] {
  const allowed = new Set(
    buildTutorialRoleChoices(roles, videoMode).flatMap((choice) => choice.roleIds),
  );
  return roleIds.filter((id) => allowed.has(id));
}
